import { canAccessLabDocs as commonCanAccessLabDocs } from '@resistapp/common/docs-access';
import { getUserProjectIds } from '@resistapp/common/access';
import { isAdmin, resistomapOrganisationId } from '@resistapp/common/features';
import { OrganisationType, OrganisationUserRole, User } from '@resistapp/common/types';
import { cityDemoProjectId, oldDemoProjectIds } from '@resistapp/common/utils';

const demoProjectIds = new Set<number>([cityDemoProjectId, ...Object.values(oldDemoProjectIds)]);

export function isResistomapAdminUser(user: User | undefined | null): boolean {
  if (!user) {
    return false;
  }
  return user.organisations.some(
    organisation =>
      organisation.id === resistomapOrganisationId &&
      organisation.type === OrganisationType.RESISTOMAP &&
      organisation.membershipRole === OrganisationUserRole.ADMIN,
  );
}

export function isProviderAdminUser(user: User | undefined | null): boolean {
  if (!user) {
    return false;
  }
  return user.organisations.some(
    organisation =>
      organisation.type === OrganisationType.PROVIDER && organisation.membershipRole === OrganisationUserRole.ADMIN,
  );
}

export function canAccessRestrictedAdminViews(user: User | undefined | null): boolean {
  if (!user) {
    return false;
  }
  return isResistomapAdminUser(user) || isProviderAdminUser(user);
}

export function isLabAdminUser(user: User | undefined | null): boolean {
  if (!user) {
    return false;
  }
  return user.organisations.some(
    organisation =>
      organisation.type === OrganisationType.LAB && organisation.membershipRole === OrganisationUserRole.ADMIN,
  );
}

export function belongsToLabOrganisation(user: User | undefined | null): boolean {
  if (!user) {
    return false;
  }
  return user.organisations.some(organisation => organisation.type === OrganisationType.LAB);
}

export function belongsToCustomerOrganisation(user: User | undefined | null): boolean {
  if (!user) {
    return false;
  }
  return user.organisations.some(organisation => organisation.type === OrganisationType.CUSTOMER);
}

// Resistomap, provider and lab organisations are the ones that run analyses and set workbooks up, so sample
// numbering is theirs to get right. Customer submitters fill in a workbook that was numbered for them and often do
// not know a reanalysis happened at all.
export function belongsToAnalysingOrganisation(user: User | undefined | null): boolean {
  if (!user) {
    return false;
  }
  if (isResistomapAdminUser(user)) {
    return true;
  }
  return user.organisations.some(
    organisation =>
      organisation.type === OrganisationType.PROVIDER || organisation.type === OrganisationType.LAB,
  );
}

export function belongsToNonDemoCustomerOrganisation(user: User | undefined | null): boolean {
  if (!user) {
    return false;
  }
  return user.organisations.some(organisation => organisation.type === OrganisationType.CUSTOMER && !organisation.isDemo);
}

export function belongsToResistomapOrProviderOrganisation(user: User | undefined | null): boolean {
  if (!user) {
    return false;
  }
  return user.organisations.some(
    organisation =>
      organisation.id === resistomapOrganisationId ||
      organisation.type === OrganisationType.RESISTOMAP ||
      organisation.type === OrganisationType.PROVIDER,
  );
}

export function belongsOnlyToDemoOrganisations(user: User | undefined | null): boolean {
  if (!user || user.organisations.length === 0) {
    return false;
  }
  return user.organisations.every(organisation => organisation.isDemo);
}

export function userHasLabWorkbookAccess(user: User | undefined | null): boolean {
  if (!user) {
    return false;
  }
  return user.organisations.some(organisation => organisation.labFeatures.length > 0);
}

export function canAccessLabSystem(user: User | undefined | null): boolean {
  return commonCanAccessLabDocs(user);
}

export function isDemoProject(projectId: number): boolean {
  return demoProjectIds.has(projectId);
}

export function userHasNonDemoProject(user: User | undefined | null): boolean {
  return getUserProjectIds(user).some(projectId => !isDemoProject(projectId));
}

export function getPreferredProjectId(user: User | undefined | null): number | null {
  const projectIds = getUserProjectIds(user);
  if (projectIds.length === 0) {
    return null;
  }
  const firstNonDemo = projectIds.find(projectId => !isDemoProject(projectId));
  return firstNonDemo ?? projectIds[0];
}

function hasPrivilegedAdminAccess(user: User | undefined | null): boolean {
  return isAdmin(user) || isResistomapAdminUser(user) || isProviderAdminUser(user);
}

export function shouldForceLabLanding(user: User | undefined | null): boolean {
  if (!user || hasPrivilegedAdminAccess(user)) {
    return false;
  }
  if (belongsOnlyToDemoOrganisations(user)) {
    return false;
  }
  if (userHasNonDemoProject(user)) {
    return false;
  }
  if (belongsToLabOrganisation(user) || belongsToCustomerOrganisation(user)) {
    return true;
  }
  const hasProjects = getUserProjectIds(user).length > 0;
  if (hasProjects) {
    return false;
  }
  return userHasLabWorkbookAccess(user);
}

export function getAdminHomePath(user: User | undefined | null): string {
  if (shouldForceLabLanding(user)) {
    return '/admin/lab-workbooks';
  }
  return canAccessRestrictedAdminViews(user) ? '/admin' : '/admin/lab-workbooks';
}

export function getAdminIndexPath(user: User | undefined | null): string {
  if (shouldForceLabLanding(user)) {
    return '/admin/lab-workbooks';
  }
  return canAccessRestrictedAdminViews(user) ? '/admin/organisations' : '/admin/lab-workbooks';
}
